import { c as _c } from "react/compiler-runtime";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Replay from "@mui/icons-material/Replay";
import SkipNext from "@mui/icons-material/SkipNext";
import SkipPrevious from "@mui/icons-material/SkipPrevious";
import { Alert, alpha, Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Provider } from "react-redux";
import { callstackSlice, selectCallstack, selectCallstackFrameIndex, selectCallstackIsPlaying, selectCallstackIsReady, selectCallstackLength, selectIsLastFrame, selectIsRootFrame } from "#/features/callstack/model/callstackSlice";
import { CompactCallstackList } from "#/features/callstack/ui/CompactCallstackList";
import { createLandingHeroPreviewStore, type LandingHeroPreviewStore } from "#/features/homePage/model/landingHeroPreviewStore";
import { usePlayerControls } from "#/features/treeViewer/hooks";
import { getPlaybackStepGroups, getPlaybackStepIndex } from "#/features/treeViewer/lib";
import { TreeViewer } from "#/features/treeViewer/ui/TreeViewer";
import type { TranslationFunctions } from "#/i18n/i18n-types";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
type HomeLandingHeroPreviewRuntimeProps = {
  LL: TranslationFunctions;
};
const PLAYBACK_INTERVAL_MS = 300;
const REPLAY_RESET_PAUSE_MS = 220;
type LandingHeroPreviewRuntimeState = {
  store: LandingHeroPreviewStore;
  errorMessage: null;
} | {
  store: null;
  errorMessage: string;
};
const getPreviewRuntimeErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unexpected landing preview initialization error.";
};
const HomeLandingHeroPreviewRuntimeInner: React.FC<HomeLandingHeroPreviewRuntimeProps> = t0 => {
  const $ = _c(127);
  const {
    LL
  } = t0;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const hasAutoStartedRef = useRef(false);
  const hasManualOverrideRef = useRef(false);
  const replayTimeoutRef = useRef(null);
  const replayRestartTimeoutRef = useRef(null);
  const {
    handleReset,
    handlePlay,
    handleReplay,
    handleStepBack,
    handleStepForward
  } = usePlayerControls();
  const isReady = useAppSelector(selectCallstackIsReady);
  const callstack = useAppSelector(selectCallstack);
  const isPlaying = useAppSelector(selectCallstackIsPlaying);
  const frameIndex = useAppSelector(selectCallstackFrameIndex);
  const callstackLength = useAppSelector(selectCallstackLength);
  const isLastFrame = useAppSelector(selectIsLastFrame);
  const isRootFrame = useAppSelector(selectIsRootFrame);
  let t1;
  if ($[0] !== callstack.frames) {
    t1 = getPlaybackStepGroups(callstack.frames);
    $[0] = callstack.frames;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const playbackStepGroups = t1;
  const activePlaybackStepIndex = getPlaybackStepIndex(playbackStepGroups, frameIndex);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (replayTimeoutRef.current !== null) {
        window.clearTimeout(replayTimeoutRef.current);
        replayTimeoutRef.current = null;
      }
      if (replayRestartTimeoutRef.current !== null) {
        window.clearTimeout(replayRestartTimeoutRef.current);
        replayRestartTimeoutRef.current = null;
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const clearReplayTimers = t2;
  let t3;
  if ($[3] !== dispatch) {
    t3 = () => {
      clearReplayTimers();
      hasManualOverrideRef.current = true;
      dispatch(callstackSlice.actions.setIsPlaying(false));
    };
    $[3] = dispatch;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const stopAutoplay = t3;
  let t4;
  if ($[5] !== handleStepBack || $[6] !== stopAutoplay) {
    t4 = () => {
      stopAutoplay();
      handleStepBack();
    };
    $[5] = handleStepBack;
    $[6] = stopAutoplay;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleManualStepBack = t4;
  let t5;
  if ($[8] !== handlePlay) {
    t5 = () => {
      hasManualOverrideRef.current = true;
      handlePlay();
    };
    $[8] = handlePlay;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleManualPlayToggle = t5;
  let t6;
  if ($[10] !== handleReplay || $[11] !== stopAutoplay) {
    t6 = () => {
      stopAutoplay();
      handleReplay();
    };
    $[10] = handleReplay;
    $[11] = stopAutoplay;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleManualReplay = t6;
  let t7;
  if ($[13] !== handleStepForward || $[14] !== stopAutoplay) {
    t7 = () => {
      stopAutoplay();
      handleStepForward();
    };
    $[13] = handleStepForward;
    $[14] = stopAutoplay;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleManualStepForward = t7;
  let t8;
  let t9;
  if ($[16] !== callstackLength || $[17] !== handlePlay || $[18] !== isReady) {
    t8 = () => {
      if (!isReady || callstackLength === 0 || hasAutoStartedRef.current || hasManualOverrideRef.current) {
        return;
      }
      hasAutoStartedRef.current = true;
      handlePlay();
    };
    t9 = [callstackLength, handlePlay, isReady];
    $[16] = callstackLength;
    $[17] = handlePlay;
    $[18] = isReady;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[21] !== dispatch || $[22] !== handleReset || $[23] !== isLastFrame || $[24] !== isPlaying || $[25] !== isReady) {
    t10 = () => {
      if (!isReady || !hasAutoStartedRef.current || hasManualOverrideRef.current || isPlaying || !isLastFrame) {
        return;
      }
      replayTimeoutRef.current = window.setTimeout(() => {
        replayTimeoutRef.current = null;
        handleReset();
        replayRestartTimeoutRef.current = window.setTimeout(() => {
          replayRestartTimeoutRef.current = null;
          if (!hasManualOverrideRef.current) {
            dispatch(callstackSlice.actions.setIsPlaying(true));
          }
        }, REPLAY_RESET_PAUSE_MS);
      }, 900);
      return clearReplayTimers;
    };
    t11 = [clearReplayTimers, dispatch, handleReset, isLastFrame, isPlaying, isReady];
    $[21] = dispatch;
    $[22] = handleReset;
    $[23] = isLastFrame;
    $[24] = isPlaying;
    $[25] = isReady;
    $[26] = t10;
    $[27] = t11;
  } else {
    t10 = $[26];
    t11 = $[27];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => () => {
      clearReplayTimers();
    };
    t13 = [clearReplayTimers];
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      minWidth: 0
    };
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      minWidth: 0
    };
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== LL) {
    t16 = LL.HOME_PILLAR_REPLAY_TITLE();
    $[32] = LL;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <Typography variant="subtitle2" color="text.secondary">{t16}</Typography>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = Math.max(activePlaybackStepIndex + 1, 0);
  const t19 = playbackStepGroups.length || callstackLength;
  let t20;
  if ($[36] !== t18 || $[37] !== t19) {
    t20 = <Typography variant="caption" color="text.secondary">Step {t18} /{" "}{t19}</Typography>;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t17 || $[40] !== t20) {
    t21 = <Stack direction="row" justifyContent="space-between" alignItems="center" sx={t15}>{t17}{t20}</Stack>;
    $[39] = t17;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== theme.appDesign.surfaceLowest) {
    t22 = alpha(theme.appDesign.surfaceLowest, 0.92);
    $[42] = theme.appDesign.surfaceLowest;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== theme.appDesign.outline) {
    t23 = alpha(theme.appDesign.outline, 0.12);
    $[44] = theme.appDesign.outline;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const t24 = `1px solid ${t23}`;
  let t25;
  if ($[46] !== t22 || $[47] !== t24) {
    t25 = {
      position: "relative",
      minHeight: 212,
      borderRadius: 3,
      bgcolor: t22,
      border: t24,
      overflow: "hidden"
    };
    $[46] = t22;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== theme.appDesign.accent) {
    t26 = alpha(theme.appDesign.accent, 0.22);
    $[49] = theme.appDesign.accent;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  const t27 = `radial-gradient(circle at 50% 20%, ${t26} 0%, transparent 46%)`;
  let t28;
  if ($[51] !== t27) {
    t28 = <Box sx={{
      position: "absolute",
      inset: 0,
      background: t27,
      pointerEvents: "none"
    }} />;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Box sx={{
      position: "relative",
      zIndex: 1,
      height: "100%",
      px: 1,
      py: 1.25
    }}><TreeViewer playbackInterval={PLAYBACK_INTERVAL_MS} binaryTreeAlign="center" /></Box>;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t25 || $[55] !== t28) {
    t30 = <Box sx={t25}>{t28}{t29}</Box>;
    $[54] = t25;
    $[55] = t28;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== theme.appDesign.surfaceLowest) {
    t31 = alpha(theme.appDesign.surfaceLowest, 0.86);
    $[57] = theme.appDesign.surfaceLowest;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== theme.appDesign.outline) {
    t32 = alpha(theme.appDesign.outline, 0.1);
    $[59] = theme.appDesign.outline;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  const t33 = `1px solid ${t32}`;
  let t34;
  if ($[61] !== t31 || $[62] !== t33) {
    t34 = {
      borderRadius: 3,
      bgcolor: t31,
      border: t33,
      overflow: "hidden"
    };
    $[61] = t31;
    $[62] = t33;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      px: 1.5,
      py: 1.25
    };
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== LL) {
    t36 = LL.CALLSTACK();
    $[65] = LL;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  let t37;
  if ($[67] !== t36) {
    t37 = <Stack direction="row" spacing={1} alignItems="center" sx={t35}><Typography variant="subtitle2" color="text.secondary">{t36}</Typography></Stack>;
    $[67] = t36;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <CompactCallstackList height={262} />;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  let t39;
  if ($[70] !== t34 || $[71] !== t37) {
    t39 = <Box sx={t34}>{t37}{t38}</Box>;
    $[70] = t34;
    $[71] = t37;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      xs: "column",
      sm: "row"
    };
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  let t41;
  let t42;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      xs: "flex-start",
      sm: "center"
    };
    t42 = {
      minWidth: 0
    };
    $[74] = t41;
    $[75] = t42;
  } else {
    t41 = $[74];
    t42 = $[75];
  }
  const t43 = !isReady || isRootFrame;
  let t44;
  if ($[76] !== theme.appDesign.surfaceHigh) {
    t44 = alpha(theme.appDesign.surfaceHigh, 0.9);
    $[76] = theme.appDesign.surfaceHigh;
    $[77] = t44;
  } else {
    t44 = $[77];
  }
  let t45;
  if ($[78] !== t44) {
    t45 = {
      bgcolor: t44
    };
    $[78] = t44;
    $[79] = t45;
  } else {
    t45 = $[79];
  }
  let t46;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <SkipPrevious fontSize="small" />;
    $[80] = t46;
  } else {
    t46 = $[80];
  }
  let t47;
  if ($[81] !== handleManualStepBack || $[82] !== t43 || $[83] !== t45) {
    t47 = <IconButton size="small" onClick={handleManualStepBack} disabled={t43} aria-label="Step back" title="Step back" sx={t45}>{t46}</IconButton>;
    $[81] = handleManualStepBack;
    $[82] = t43;
    $[83] = t45;
    $[84] = t47;
  } else {
    t47 = $[84];
  }
  const t48 = isLastFrame && !isPlaying ? handleManualReplay : handleManualPlayToggle;
  const t49 = !callstackLength;
  const t50 = isLastFrame && !isPlaying ? "Replay" : isPlaying ? "Pause" : "Play";
  const t51 = isLastFrame && !isPlaying ? "Replay" : isPlaying ? "Pause" : "Play";
  let t52;
  if ($[85] !== theme.appDesign.accent) {
    t52 = alpha(theme.appDesign.accent, 0.14);
    $[85] = theme.appDesign.accent;
    $[86] = t52;
  } else {
    t52 = $[86];
  }
  let t53;
  if ($[87] !== t52 || $[88] !== theme.appDesign.accentSoft) {
    t53 = {
      bgcolor: t52,
      color: theme.appDesign.accentSoft
    };
    $[87] = t52;
    $[88] = theme.appDesign.accentSoft;
    $[89] = t53;
  } else {
    t53 = $[89];
  }
  let t54;
  if ($[90] !== isLastFrame || $[91] !== isPlaying) {
    t54 = isLastFrame && !isPlaying ? <Replay fontSize="small" /> : isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />;
    $[90] = isLastFrame;
    $[91] = isPlaying;
    $[92] = t54;
  } else {
    t54 = $[92];
  }
  let t55;
  if ($[93] !== t48 || $[94] !== t49 || $[95] !== t50 || $[96] !== t51 || $[97] !== t53 || $[98] !== t54) {
    t55 = <IconButton size="small" onClick={t48} disabled={t49} aria-label={t50} title={t51} sx={t53}>{t54}</IconButton>;
    $[93] = t48;
    $[94] = t49;
    $[95] = t50;
    $[96] = t51;
    $[97] = t53;
    $[98] = t54;
    $[99] = t55;
  } else {
    t55 = $[99];
  }
  const t56 = !isReady || isLastFrame;
  let t57;
  if ($[100] !== theme.appDesign.surfaceHigh) {
    t57 = alpha(theme.appDesign.surfaceHigh, 0.9);
    $[100] = theme.appDesign.surfaceHigh;
    $[101] = t57;
  } else {
    t57 = $[101];
  }
  let t58;
  if ($[102] !== t57) {
    t58 = {
      bgcolor: t57
    };
    $[102] = t57;
    $[103] = t58;
  } else {
    t58 = $[103];
  }
  let t59;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <SkipNext fontSize="small" />;
    $[104] = t59;
  } else {
    t59 = $[104];
  }
  let t60;
  if ($[105] !== handleManualStepForward || $[106] !== t56 || $[107] !== t58) {
    t60 = <IconButton size="small" onClick={handleManualStepForward} disabled={t56} aria-label="Step forward" title="Step forward" sx={t58}>{t59}</IconButton>;
    $[105] = handleManualStepForward;
    $[106] = t56;
    $[107] = t58;
    $[108] = t60;
  } else {
    t60 = $[108];
  }
  let t61;
  if ($[109] !== t47 || $[110] !== t55 || $[111] !== t60) {
    t61 = <Stack direction="row" spacing={0.5}>{t47}{t55}{t60}</Stack>;
    $[109] = t47;
    $[110] = t55;
    $[111] = t60;
    $[112] = t61;
  } else {
    t61 = $[112];
  }
  let t62;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = {
      sm: 0.5
    };
    $[113] = t62;
  } else {
    t62 = $[113];
  }
  let t63;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = {
      ml: t62,
      lineHeight: 1.6,
      maxWidth: {
        xs: "100%",
        sm: 220
      }
    };
    $[114] = t63;
  } else {
    t63 = $[114];
  }
  let t64;
  if ($[115] !== LL) {
    t64 = LL.HOME_PILLAR_VIS_BODY();
    $[115] = LL;
    $[116] = t64;
  } else {
    t64 = $[116];
  }
  let t65;
  if ($[117] !== t64) {
    t65 = <Typography variant="caption" color="text.secondary" sx={t63}>{t64}</Typography>;
    $[117] = t64;
    $[118] = t65;
  } else {
    t65 = $[118];
  }
  let t66;
  if ($[119] !== t61 || $[120] !== t65) {
    t66 = <Stack direction={t40} spacing={1} alignItems={t41} sx={t42}>{t61}{t65}</Stack>;
    $[119] = t61;
    $[120] = t65;
    $[121] = t66;
  } else {
    t66 = $[121];
  }
  let t67;
  if ($[122] !== t21 || $[123] !== t30 || $[124] !== t39 || $[125] !== t66) {
    t67 = <Stack spacing={1.5} sx={t14}>{t21}{t30}{t39}{t66}</Stack>;
    $[122] = t21;
    $[123] = t30;
    $[124] = t39;
    $[125] = t66;
    $[126] = t67;
  } else {
    t67 = $[126];
  }
  return t67;
};
export const HomeLandingHeroPreviewRuntime: React.FC<HomeLandingHeroPreviewRuntimeProps> = t0 => {
  const $ = _c(8);
  const {
    LL
  } = t0;
  const [runtimeState] = useState(_temp);
  if (!runtimeState.store) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Typography variant="subtitle2">Landing preview failed to load.</Typography>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== runtimeState.errorMessage) {
      t2 = <Alert severity="error" variant="outlined">{t1}<Typography variant="caption">{runtimeState.errorMessage}</Typography></Alert>;
      $[1] = runtimeState.errorMessage;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== LL) {
    t1 = <HomeLandingHeroPreviewRuntimeInner LL={LL} />;
    $[3] = LL;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== runtimeState.store || $[6] !== t1) {
    t2 = <Provider store={runtimeState.store}>{t1}</Provider>;
    $[5] = runtimeState.store;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp() {
  ;
  try {
    return {
      store: createLandingHeroPreviewStore(),
      errorMessage: null
    };
  } catch (t0) {
    const error = t0;
    return {
      store: null,
      errorMessage: getPreviewRuntimeErrorMessage(error)
    };
  }
}